import { TechIcon } from '../lib/icons/TechIcon';
import type { Template } from '../data/templates';

const SIZES = {
  sm: { circle: 'h-8 w-8', icon: 15, counter: 'text-[11px]' },
  lg: { circle: 'h-10 w-10', icon: 18, counter: 'text-xs' },
} as const;

interface TemplateLogoStackProps {
  template: Template;
  size?: keyof typeof SIZES;
}

export function TemplateLogoStack({ template, size = 'sm' }: TemplateLogoStackProps) {
  const nodes = template.nodes as Array<{
    id: string;
    data?: { label?: string; icon?: string; hex?: string };
  }>;
  if (!nodes.length) return null;

  const visibleNodes = nodes.slice(0, nodes.length <= 4 ? 4 : 3);
  const remaining = nodes.length - visibleNodes.length;
  const styles = SIZES[size];

  return (
    <div className="flex items-center justify-center px-3">
      {visibleNodes.map((node, index) => {
        const raw = node.data?.hex;
        const hex = raw ? raw.replace(/^#/, '') : undefined;
        return (
          <span
            key={node.id}
            className={`grid ${styles.circle} flex-none place-items-center rounded-full border-2 border-card shadow-sm ${index === 0 ? '' : '-ml-2'}`}
            style={{ background: hex ? `color-mix(in oklab, #${hex} 20%, var(--card))` : 'var(--card)' }}
          >
            <TechIcon
              icon={node.data?.icon || 'si:codesandbox'}
              color={hex}
              size={styles.icon}
              alt={node.data?.label || 'Technology'}
            />
          </span>
        );
      })}
      {remaining > 0 && (
        <span
          className={`-ml-2 grid ${styles.circle} ${styles.counter} flex-none place-items-center rounded-full border-2 border-card bg-muted font-semibold text-muted-foreground shadow-sm`}
          aria-label={`${remaining} more technologies`}
        >
          +{remaining}
        </span>
      )}
    </div>
  );
}
